// Native ad consent (Google UMP + iOS ATT). The message lives in AdMob → Privacy &
// messaging; every AdMob request must await gatherAdConsent() first.

const getAdMob = () => window.Capacitor?.Plugins?.AdMob;

let consentPromise = null;
let privacyOptionsRequired = false;

const FORM_ATTEMPTS = 6;
const FORM_RETRY_MS = 1000;

// At launch the native screen may not exist yet ("No ViewController"), so retry briefly.
async function showConsentFormWhenReady(AdMob) {
    for (let attempt = 1; ; attempt++) {
        try {
            return await AdMob.showConsentForm();
        } catch (err) {
            if (attempt >= FORM_ATTEMPTS) throw err;
            await new Promise((resolve) => setTimeout(resolve, FORM_RETRY_MS));
        }
    }
}

async function requestConsent() {
    const AdMob = getAdMob();
    if (!AdMob || typeof AdMob.requestConsentInfo !== 'function') {
        return { canRequestAds: true };
    }
    let info;
    try {
        // The plugin only attaches its consent forms to the app screen in initialize().
        await AdMob.initialize({ initializeForTesting: process.env.NODE_ENV === 'development' });
        info = await AdMob.requestConsentInfo();
        if (info?.isConsentFormAvailable && info?.status === 'REQUIRED'
            && typeof AdMob.showConsentForm === 'function') {
            info = await showConsentFormWhenReady(AdMob);
        }
        privacyOptionsRequired = info?.privacyOptionsRequirementStatus === 'REQUIRED';

        // ATT only after the consent form, as Google recommends.
        const platform = window.Capacitor?.getPlatform?.();
        if (platform === 'ios' && info?.canRequestAds !== false
            && typeof AdMob.requestTrackingAuthorization === 'function') {
            await AdMob.requestTrackingAuthorization();
        }
        return info || { canRequestAds: true };
    } catch (err) {
        // A failure is never consent: only ads that UMP already allowed may run.
        console.warn('Ad consent flow failed:', err);
        return { ...info, canRequestAds: info?.canRequestAds === true };
    }
}

/** Runs the consent flow once per app launch; resolves to the UMP consent info. */
export function gatherAdConsent() {
    if (!consentPromise) consentPromise = requestConsent();
    return consentPromise;
}

/** True when the user is in a region where they must be able to change consent. */
export function isAdPrivacyOptionsRequired() {
    return privacyOptionsRequired;
}

/** Reopens Google's consent form so the user can change or withdraw consent. */
export async function showAdPrivacyOptions() {
    const AdMob = getAdMob();
    if (!AdMob || typeof AdMob.showPrivacyOptionsForm !== 'function') return false;
    try {
        await gatherAdConsent();
        await AdMob.showPrivacyOptionsForm();
        return true;
    } catch (err) {
        console.warn('Ad privacy options unavailable:', err);
        return false;
    }
}
